import { useState, useCallback, useEffect, useRef } from "react";
import {
  Upload,
  FileSpreadsheet,
  Download,
  Check,
  X,
  Clock,
  Loader2,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BulkUploadTab() {
  const [isDragging, setIsDragging] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [verifyGenerated, setVerifyGenerated] = useState(false);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const fetchJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/bulk/jobs`);
      setJobs(response.data);
      // Set the most recent job as current if exists
      if (response.data.length > 0 && !currentJob) {
        setCurrentJob(response.data[0]);
      }
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
    }
  }, [currentJob]);

  const fetchJobStatus = useCallback(async (jobId) => {
    try {
      const response = await axios.get(`${API_URL}/bulk/status/${jobId}`);
      setCurrentJob(response.data);
      
      // Update jobs list
      setJobs((prev) =>
        prev.map((j) => (j.job_id === jobId ? response.data : j))
      );

      if (response.data.status === "completed") {
        toast.success("File enrichment completed!");
        fetchJobs();
      } else if (response.data.status === "failed") {
        toast.error(`Processing failed: ${response.data.error_message}`);
      }
    } catch (error) {
      console.error("Failed to fetch job status:", error);
    }
  }, [fetchJobs]);

  const uploadFile = useCallback(async (file) => {
    // Validate file type
    const validTypes = [".csv", ".xlsx", ".xls"];
    const extension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    
    if (!validTypes.includes(extension)) {
      toast.error("Please upload a CSV or Excel file");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API_URL}/bulk/upload?verify_generated=${verifyGenerated}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("File uploaded! Processing started...");
      
      // Fetch the new job status
      const jobResponse = await axios.get(
        `${API_URL}/bulk/status/${response.data.job_id}`
      );
      setCurrentJob(jobResponse.data);
      fetchJobs();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [fetchJobs, verifyGenerated]);

  // Fetch all jobs on mount
  useEffect(() => {
    fetchJobs();
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchJobs]);

  // Poll for job status when there's a processing job
  useEffect(() => {
    if (currentJob?.status === "processing" || currentJob?.status === "pending") {
      pollIntervalRef.current = setInterval(() => {
        fetchJobStatus(currentJob.job_id);
      }, 2000);
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [currentJob?.job_id, currentJob?.status, fetchJobStatus]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      uploadFile(file);
    }
  }, [uploadFile]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };


  const downloadFile = async (jobId) => {
    try {
      const response = await axios.get(`${API_URL}/bulk/download/${jobId}`, {
        responseType: "blob",
      });

      // Get filename from response headers or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "enriched_file.xlsx";
      if (contentDisposition) {
        // Handle both quoted and unquoted filenames
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "").trim();
        }
      }

      // Determine correct MIME type based on filename
      let mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      if (filename.toLowerCase().endsWith('.csv')) {
        mimeType = "text/csv";
      }

      // Create download link with correct blob type
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("File downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
            <Check className="w-3 h-3 mr-1" />
            Completed
          </Badge>
        );
      case "processing":
        return (
          <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Processing
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
            <X className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-8">
      {/* Upload Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Drop Zone */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-card">
          <h2 className="font-heading font-bold text-xl text-ocean-900 mb-4">
            Upload File for Enrichment
          </h2>
          <p className="text-slate-500 text-sm mb-6">
            Upload a CSV or Excel file containing vessel names, company names, or
            IMO numbers. We'll find and attach matching email addresses.
          </p>

          <div
            data-testid="drop-zone"
            className={`upload-zone cursor-pointer ${
              isDragging ? "active" : ""
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            
            {isUploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="w-12 h-12 text-signal-orange animate-spin mb-4" />
                <p className="text-ocean-900 font-medium">Uploading file...</p>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-ocean-900 font-medium mb-1">
                  Drag & drop your file here
                </p>
                <p className="text-slate-500 text-sm mb-4">
                  or click to browse files
                </p>
                <Badge variant="secondary" className="bg-slate-100">
                  CSV, XLSX supported
                </Badge>
              </>
            )}
          </div>

          <div className="mt-6 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-ocean-900">
                Verify Generated Emails
              </p>
              <p className="text-xs text-slate-500">
                Confirmed emails are always verified
              </p>
            </div>
            <Switch
              checked={verifyGenerated}
              onCheckedChange={setVerifyGenerated}
              data-testid="verify-generated-switch"
            />
          </div>
        </div>

        {/* Current Job Status */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-xl text-ocean-900">
              Processing Status
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchJobs}
              className="text-slate-500 hover:text-ocean-900"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {currentJob ? (
            <div className="space-y-6">
              {/* File Info */}
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <FileSpreadsheet className="w-8 h-8 text-signal-orange flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-ocean-900 truncate">
                    {currentJob.filename}
                  </p>
                  <p className="text-sm text-slate-500">
                    Uploaded {formatDate(currentJob.created_at)}
                  </p>
                </div>
                {getStatusBadge(currentJob.status)}
              </div>

              {/* Progress */}
              {(currentJob.status === "processing" ||
                currentJob.status === "completed") && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Progress</span>
                    <span className="font-mono text-ocean-900">
                      {currentJob.emails_found + currentJob.emails_not_found} /{" "}
                      {currentJob.total_rows || "..."}
                    </span>
                  </div>
                  <Progress
                    value={
                      currentJob.total_rows
                        ? ((currentJob.emails_found + currentJob.emails_not_found) /
                            currentJob.total_rows) *
                          100
                        : 0
                    }
                    className="h-2"
                  />
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="stats-card text-center">
                  <p className="text-2xl font-bold text-ocean-900 font-mono">
                    {currentJob.total_rows || 0}
                  </p>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">
                    Total Rows
                  </p>
                </div>
                <div className="stats-card text-center">
                  <p className="text-2xl font-bold text-green-600 font-mono">
                    {currentJob.emails_found || 0}
                  </p>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">
                    Emails Found
                  </p>
                </div>
                <div className="stats-card text-center">
                  <p className="text-2xl font-bold text-red-500 font-mono">
                    {currentJob.emails_not_found || 0}
                  </p>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">
                    Not Found
                  </p>
                </div>
              </div>

              {/* Match Percentage */}
              {currentJob.status === "completed" && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-green-900">Match Success Rate</p>
                      <p className="text-sm text-green-700">
                        Processing completed successfully
                      </p>
                    </div>
                    <span className="text-3xl font-bold font-mono text-green-600">
                      {currentJob.match_percentage}%
                    </span>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {currentJob.status === "failed" && currentJob.error_message && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-medium text-red-900">Processing Failed</p>
                  <p className="text-sm text-red-700 mt-1">
                    {currentJob.error_message}
                  </p>
                </div>
              )}

              {/* Download Button */}
              {currentJob.status === "completed" && (
                <Button
                  data-testid="download-button"
                  onClick={() => downloadFile(currentJob.job_id)}
                  className="w-full h-12 bg-signal-orange hover:bg-signal-orange/90 text-white font-semibold"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Enriched File
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                No files uploaded yet. Upload a file to get started.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Jobs */}
      {jobs.length > 1 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-lg text-ocean-900">
              Recent Jobs
            </h3>
          </div>
          <div className="divide-y divide-slate-100">
            {jobs.slice(1, 6).map((job) => (
              <div
                key={job.job_id}
                className="px-6 py-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <FileSpreadsheet className="w-6 h-6 text-slate-400" />
                  <div>
                    <p className="font-medium text-ocean-900 text-sm">
                      {job.filename}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatDate(job.created_at)} • {job.total_rows} rows
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-600">
                    {job.match_percentage}% matched
                  </span>
                  {getStatusBadge(job.status)}
                  {job.status === "completed" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadFile(job.job_id)}
                      className="text-signal-orange hover:text-signal-orange/80"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
